import React, { useState, useEffect, useRef } from 'react';
import { toBlob } from 'html-to-image';
import { getTeamLogoUrl, getTeamLogoUrl2, getTeamLogoUrl3, applyHeadToHeadTiebreakers } from '../utils/helpers';
import { logos } from '../teams/logos';
import { Settings, Info, Send, Calendar, RefreshCw, Trophy, FlaskConical } from 'lucide-react';
import '../bracket.css';

// Teams requiring white logo inverted rendering
const REG_LOGO = new Set([
    '', 'Oklahoma', 'Ole Miss', 'UAB', 'Coastal Carolina', 'Florida', 'Eastern Michigan', 'Illinois', 'Kansas State',
    'Iowa', 'Liberty', 'Kent State', 'Maryland', 'Marshall', 'Memphis', 'Massachusetts', 'Kennesaw State', 'Northwestern', 'Ohio',
    'Oklahoma State', 'Rutgers', 'Georgia', 'San Diego State', 'SMU', 'TCU', 'Stanford', 'Southern Miss', 'UCLA',
    'Troy', 'Tulane', 'UConn', 'Wake Forest', 'UTSA', 'Washington State', 'Western Kentucky', 'West Virginia', 'Wyoming',
    'Boise State', 'Penn State', ""
]);

// Teams requiring logo color swaps
const ALTERNATE_COLOR = new Set([
    'Miami', 'Texas Tech', '', 'USC', 'Iowa', 'Colorado', 'Minnesota', 'Rutgers', 'Houston', 'North Dakota State', 'Wyoming'
    , 'Syracuse', 'UAB', 'UCLA', 'UTEP', 'UL Monroe', 'UTSA', 'Penn State', 'Arizona State', 'Illinois', "Hawai'i", '', ''
]);

// Teams using regular logo versions
const WHITE_BACKGROUND = new Set([
    'San Diego State', 'SMU', 'Texas State'
]);

const WHITE_LOGO = new Set([
    'Mississippi State', 'Oklahoma', 'Eastern Michigan', 'Georgia Tech', '', 'Kansas State', '', 'Northwestern',
    'TCU', '', 'Washington State'
]);
const shiftN = 12 * 7;

// Helper selectors
const SizeSelector = ({ setSize, size = 12 }) => (
  <label>
    <select value={size} onChange={e => setSize(Number(e.target.value))}>
      <option value={12}>12 Team Playoff</option>
      <option value={4}>4 Team Playoff</option>
      <option value={8}>8 Team Playoff</option>
      <option value={16}>16 Team Playoff</option>
      <option value={24}>24 Team Playoff</option>
      <option value={32}>32 Team Playoff</option>
    </select>
  </label>
);

const AQSelector = ({ AQ, setAQ, size, year }) => {
  const adj = year == 2025 || year == 2024 ? 10 : 11;
  const options = Array.from({ length: Math.min(size + 1, adj) }, (_, i) => i);
  if (AQ > size) setAQ(size);
  return (
    <label>
      <select value={AQ} onChange={e => setAQ(Number(e.target.value))}>
        {options.map(opt => (
          <option key={opt} value={opt}>
            {opt} Conference Champ Bids
          </option>
        ))}
      </select>
    </label>
  );
};

const waitForImages = async (element) => {
  const images = Array.from(element.querySelectorAll('img'));
  await Promise.all(
    images.map(async (img) => {
      if (img.complete && img.naturalWidth > 0) {
        try { await img.decode(); } catch {}
        return;
      }
      await new Promise(resolve => {
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      });
      try { await img.decode(); } catch {}
    })
  );
};

const BubbleTeams = ({ teams, listOfTeams }) => {
  if (!teams?.length) return null;
  return (
    <h2 style={{ textAlign: 'center' }}>
      The Bubble:{' '}
      {teams.map(team => (
        <div className="bubble" key={team}>
          <img src={getTeamLogoUrl(listOfTeams[team])} alt={team} />
        </div>
      ))}
    </h2>
  );
};

// Bracket round definitions
const rounds = [[1, 8, 9], [4, 5, 12], [2, 7, 10], [3, 6, 11]];
const rounds_new = [[4, 12, 5], [1, 9, 8], [3, 11, 6], [2, 10, 7]];
const rounds4 = [[1, 4], [2, 3]];
const rounds8 = [[1, 8], [4, 5], [3, 6], [2, 7]];
const rounds16 = [[1, 16], [8, 9], [5, 12], [4, 13], [6, 11], [3, 14], [7, 10], [2, 15]];
const rounds32a = [[1, 32], [16, 17], [8, 25], [9, 24], [5, 28], [12, 21], [13, 20], [4, 29]];
const rounds32b = [[2, 31], [15, 18], [7, 26], [10, 23], [6, 27], [11, 22], [14, 19], [3, 30]];
const rounds24a = [[1, 16, 17], [8, 9, 24], [4, 13, 20], [5, 12, 21]];
const rounds24b = [[2, 15, 18], [7, 10, 23], [3, 14, 19], [6, 11, 22]];

const POWER4 = new Set(['ACC', 'Big Ten', 'Big 12', 'SEC']);
const GROUP6 = new Set([
  'American', 'American Athletic', 'Conference USA', 'CUSA',
  'MAC', 'Mid-American', 'Mountain West', 'Pac-12', 'Sun Belt'
]);

const PlayoffProjection = ({ teams, grades, records, listOfTeams, final = false, year, dark = false, logo_type, set_logo_type, YearMenu, loading, simResults, mSwitch, setMSwitch }) => {
  const size = year >= 2024 ? 12 : 4;
  const auto = year >= 2024 ? 5 : 0;

  const [requiredPlayoffSpots, setSize] = useState(size);
  const [AQ, setAQ] = useState(auto);
  const [isSharing, setIsSharing] = useState(false);
  const [settings, setSettings] = useState(false);
  const [help, setHelp] = useState(false);
  const [archive, setArchive] = useState(year < 2026);

  useEffect(() => {
        setAQ(year >= 2024 ? 5 : 0);
        setSize(year >= 2024 ? 12 : 4);
  }, [year])


  const shareCardRef = useRef(null);

  if (!teams?.length) return null;

    // Fix 1: Deep copy team objects to avoid mutating props across renders


    const rawSorted = teams
        .slice()
        .sort((a, b) => (grades?.[b.team] ?? 0) - (grades?.[a.team] ?? 0))
        .map(t => ({ ...t }));

    // 2. Pass H2H results map if available (from props or simResults)
    // h2hResults format example: { "Georgia_vs_Texas": "Georgia" }
    const h2hData = simResults?.h2hResults || {};

    // 3. Apply explicit H2H tiebreaker pass within a small threshold (e.g., 0.001)
    const sortedTeams = applyHeadToHeadTiebreakers(rawSorted, grades, 1);

    //const sortedTeams = teams.sort((a, b) => grades?.[b.team] - grades?.[a.team]).map(t => ({ ...t }));

    /*
    const sortedByName = [...teams].sort((a, b) => a.team.localeCompare(b.team));
    // 2. Rotate the array by n positions (handles positive and negative n)
    const n = ((shiftN % sortedByName.length) + sortedByName.length) % sortedByName.length;
    sortedTeams = [...sortedByName.slice(n), ...sortedByName.slice(0, n)].map(t => ({ ...t }));*/

  // Conference champs determination
  const conferencesNeedingChamps = new Set();
  sortedTeams.forEach(t => {
    if (t.conference !== 'FBS Independents') conferencesNeedingChamps.add(t.conference);
  });
  sortedTeams.forEach(t => {
    if (t.conferenceChamp === true) conferencesNeedingChamps.delete(t.conference);
  });
  sortedTeams.forEach((team, i) => {
    if (conferencesNeedingChamps.has(team.conference)) {
      sortedTeams[i] = { ...team, conferenceChamp: true };
      conferencesNeedingChamps.delete(team.conference);
    }
  });

    // Selection Logic
  
  let topTeams = [];
  if (year >= 2026) {
    const selected = new Set();
    const findBest = predicate => sortedTeams.find(t => !selected.has(t.team) && predicate(t));

    const p4Champs = Array.from(POWER4)
      .map(conf => findBest(t => t.conference === conf && t.conferenceChamp))
      .filter(Boolean)
      .sort((a, b) => sortedTeams.indexOf(a) - sortedTeams.indexOf(b));

    const g6Champs = Array.from(GROUP6)
      .map(conf => findBest(t => t.conference === conf && t.conferenceChamp))
      .filter(Boolean)
          .sort((a, b) => sortedTeams.indexOf(a) - sortedTeams.indexOf(b));



      const maxGuaranteed = Math.min(AQ, requiredPlayoffSpots);

      let guaranteedCount = 0;

    // Check Notre Dame
    const ndRank = sortedTeams.findIndex(t => t.team === 'Notre Dame') + 1;
    if (ndRank > 0 && ndRank <= requiredPlayoffSpots) {
      const nd = sortedTeams.find(t => t.team === 'Notre Dame');
      if (nd) {
        topTeams.push(nd);
        selected.add('Notre Dame');
      }
    }

    for (const champ of p4Champs) {
      if (guaranteedCount >= maxGuaranteed) break;
      if (!selected.has(champ.team)) {
        topTeams.push(champ);
        selected.add(champ.team);
        guaranteedCount++;
      }
    }


      for (const champ of g6Champs) {
      if (guaranteedCount >= maxGuaranteed) break;
          if (!selected.has(champ.team)) {

        topTeams.push(champ);
        selected.add(champ.team);
        guaranteedCount++;
      }
    }

    for (let i = 0; i < sortedTeams.length && topTeams.length < requiredPlayoffSpots; i++) {
      const t = sortedTeams[i];
      if (!selected.has(t.team)) {
        topTeams.push(t);
        selected.add(t.team);
      }
    }

    topTeams.sort((a, b) => sortedTeams.indexOf(a) - sortedTeams.indexOf(b));
  } else {
    let representedConfs = [];
    for (let i = 0; i < sortedTeams.length && topTeams.length < requiredPlayoffSpots; i++) {
      const t = sortedTeams[i];
      if (
        requiredPlayoffSpots - topTeams.length > AQ - representedConfs.length ||
        (!representedConfs.includes(t.conference) &&
          t.conference !== 'FBS Independents' &&
          !(t.conference === 'Pac-12' && (year === 2024 || year === 2025)) &&
          (t.conferenceChamp === true || !final))
      ) {
        topTeams.push(t);
        if (!representedConfs.includes(t.conference) && t.conference !== 'FBS Independents' && (t.conferenceChamp || !final)) {
          representedConfs.push(t.conference);
        }
      }
    }
  }
  
    topTeams = topTeams.slice(0, requiredPlayoffSpots).map((t, i) => ({
    ...t,
    seed: i + 1,
    trend: '',
    record: `(${t.wins}-${t.gp - t.wins})`
  }));

  const bubbleTeams = sortedTeams
    .filter(t => !topTeams.some(tt => tt.team === t.team))
    .slice(0, Math.min(7, Math.ceil(requiredPlayoffSpots / 3)))
    .map(t => t.team);

  const handleNativeShare = async () => {
    if (!shareCardRef.current) return;
    setIsSharing(true);

    try {
      await waitForImages(shareCardRef.current);
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const blob = await toBlob(shareCardRef.current, {
  quality: 0.95,
  cacheBust: true,
  includeQueryParams: true,   // ← add this
  pixelRatio: 2,
  backgroundColor: dark ? '#111' : '#fff'
});

      if (!blob) throw new Error('Failed to generate image.');

      const file = new File([blob], 'cfp-projection.png', { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'CFP Playoff Projection',
          text: requiredPlayoffSpots == 12 ? `My ${requiredPlayoffSpots}-Team College Football Playoff Prediction` : `My Hypothetical ${requiredPlayoffSpots}-Team College Football Playoff Prediction!`,
          files: [file]
        });
      } else {
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'cfp-projection.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error executing share:', err);
      }
    } finally {
      setIsSharing(false);
    }
  };

    const renderBracket = (roundsArr, extraClass = '', both = false, right = false) => (
        <div className={`bracket ${extraClass}`}>
            {roundsArr.map((round, roundIndex) => (
                <div className="round" key={roundIndex}>
                    {round.map((seed, matchIndex) => {
                        const team = topTeams.find(t => t.seed === seed);
                        if (!team) return null;

                        // Helper to calculate seed display label based on `both` flag
                        const getSeedLabel = (s) => (!both ? s : s % 2 === 0 ? s / 2 : (s + 1) / 2);

                        // Top seeds get a BYE in a 12-team structure
                        if (matchIndex === 0) {
                            return (
                                <div className="matchup" key={matchIndex}>
                                    <div className="bye">
                                        <div className="team">
                                            <div className="teamInfo">
                                                <span className="seed">{getSeedLabel(team.seed)}</span>
                                                <img
                                                    key={team.team}
                                                    src={getTeamLogoUrl2(listOfTeams[team.team], team.team, logos, logo_type)}
                                                    alt={team.team}
                                                />
                                                &nbsp;{team.team} <span className="record">({records?.[team.team]})</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        // First-round active head-to-head matchup
                        if (matchIndex === 1) {
                            const nextTeamSeed = round[matchIndex + 1];
                            const nextTeam = topTeams.find(t => t.seed === nextTeamSeed);

                            return (
                                <div className="matchup" key={matchIndex}>
                                    <div className="first">
                                        <div className="team">
                                            <div className="teamInfo">
                                                <span className="seed">{getSeedLabel(team.seed)}</span>
                                                <img
                                                    key={team.team}
                                                    src={getTeamLogoUrl2(listOfTeams[team.team], team.team, logos, logo_type)}
                                                    alt={team.team}
                                                />
                                                &nbsp;{team.team} <span className="record">({records?.[team.team]})</span>
                                            </div>
                                        </div>
                                        {nextTeam && (
                                            <div className="team">
                                                <div className="teamInfo">
                                                    <span className="seed">{getSeedLabel(nextTeam.seed)}</span>
                                                    <img
                                                        key={nextTeam.team}
                                                        src={getTeamLogoUrl2(listOfTeams[nextTeam.team], nextTeam.team, logos, logo_type)}
                                                        alt={nextTeam.team}
                                                    />
                                                    &nbsp;{nextTeam.team} <span className="record">({records?.[nextTeam.team]})</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        return null;
                    })}
                </div>
            ))}
        </div>
    );


    const HISTORICAL_BRACKETS = {
        2024: {
            secondRound: [
                { topSeed: 5, bottomSeed: 4 },
                { topSeed: 8, bottomSeed: 1 },
                { topSeed: 6, bottomSeed: 3 },
                { topSeed: 7, bottomSeed: 2 },
            ],
            thirdRound: [
                { topSeed: 5, bottomSeed: 8 },
                { topSeed: 6, bottomSeed: 7 },
            ],
            finalRound: [
                { topSeed: 8, bottomSeed: 7 },
            ]
        },
        2025: {
            secondRound: [
                { topSeed: 5, bottomSeed: 4 },
                { topSeed: 9, bottomSeed: 1 },
                { topSeed: 6, bottomSeed: 3 },
                { topSeed: 10, bottomSeed: 2 },
            ],
            thirdRound: [
                { topSeed: 5, bottomSeed: 1 },
                { topSeed: 6, bottomSeed: 10 },
            ],
            finalRound: [
                { topSeed: 1, bottomSeed: 10 },
            ]
        }
    };

    const newRenderBracket = (roundsArr, year = 2026) => {
        // 1. Build a seed lookup map dynamically
        const teamsBySeed = topTeams.reduce((acc, t) => {
            if (t?.seed) acc[t.seed] = t;
            return acc;
        }, {});

        // Helper to determine the winning seed between two seeds based on team.avg
        const getWinnerSeed = (seedA, seedB) => {
            const teamA = teamsBySeed[seedA];
            const teamB = teamsBySeed[seedB];
            return null;
            if (!teamA) return seedB;
            if (!teamB) return seedA;

            // Compare team averages (higher average advances; ties default to better seed)
            const avgA = teamA.avg ?? 0;
            const avgB = teamB.avg ?? 0;

            if (avgA === avgB) {
                return seedA < seedB ? seedA : seedB;
            }
            return avgA > avgB ? seedA : seedB;
        };

        // Helper to safely render team slots
        const renderTeamSlot = (seed) => {
            const team = teamsBySeed[seed];
            if (!team) {
                return (
                    <div className="bracket-team">
                        <div className="bracket-team-seed" />
                        <div className="bracket-team-logo" />
                    </div>
                );
            }

            const isCurrentYear = year === 2026;
            const logoUrl = listOfTeams[team.team]
                ? getTeamLogoUrl3(listOfTeams[team.team], !isCurrentYear || REG_LOGO.has(team.team))
                : '';

            const bgColor = WHITE_BACKGROUND.has(team.team)
                ? 'white'
                : ALTERNATE_COLOR.has(team.team)
                    ? team.alternateColor
                    : team.color;

            const logoFilter = WHITE_LOGO.has(team.team) && isCurrentYear
                ? 'brightness(0) invert(1)'
                : 'none';


            return (
                <div className={`bracket-team ${team.conferenceChamp ? 'automatic' : ''}`.trim()}>
                    <div className="bracket-team-seed">{seed}</div>
                    <div className="bracket-team-logo" style={{ backgroundColor: bgColor }}>
                        <img style={{ filter: logoFilter }} key={team.team} src={logoUrl} alt={team.team} />
                    </div>
                </div>
            );
        };

        // 2. Load historical seed overrides if available
        const history = HISTORICAL_BRACKETS[year];

        // 3. Define or dynamically simulate rounds
        const firstRoundMatchups = [
            { topSeed: 12, bottomSeed: 5 },
            { topSeed: 9, bottomSeed: 8 },
            { topSeed: 11, bottomSeed: 6 },
            { topSeed: 10, bottomSeed: 7 }
        ];

        // If no history, simulate winners from round to round
        const r1Winners = firstRoundMatchups.map(m => getWinnerSeed(m.topSeed, m.bottomSeed));

        const secondRoundMatchups = history?.secondRound || [
            { topSeed: r1Winners[0], bottomSeed: 4 }, // 12 vs 5 winner plays 4
            { topSeed: r1Winners[1], bottomSeed: 1 }, // 9 vs 8 winner plays 1
            { topSeed: r1Winners[2], bottomSeed: 3 }, // 11 vs 6 winner plays 3
            { topSeed: r1Winners[3], bottomSeed: 2 }  // 10 vs 7 winner plays 2
        ];

        const r2Winners = secondRoundMatchups.map(m => getWinnerSeed(m.topSeed, m.bottomSeed));

        const thirdRoundMatchups = history?.thirdRound || [
            { topSeed: r2Winners[0], bottomSeed: r2Winners[1] },
            { topSeed: r2Winners[2], bottomSeed: r2Winners[3] }
        ];

        const r3Winners = thirdRoundMatchups.map(m => getWinnerSeed(m.topSeed, m.bottomSeed));

        const finalRoundMatchups = history?.finalRound || [
            { topSeed: r3Winners[0], bottomSeed: r3Winners[1] }
        ];

        const rounds = [
            { key: 'first-round', title: 'First Round', matchups: firstRoundMatchups },
            { key: 'second-round', title: 'Quarterfinals', matchups: secondRoundMatchups },
            { key: 'third-round', title: 'Semifinals', matchups: thirdRoundMatchups },
            { key: 'final-round', title: <>National<br />Championship</>, matchups: finalRoundMatchups }
        ];

        return (
            <div className="cfp-bracket">
                {rounds.map(round => (
                    <div key={round.key} className={`bracket-${round.key}`}>
                        <div className="bracket-round-title">{round.title}</div>
                        <div className="bracket-matchups">
                            {round.matchups.map((matchup, idx) => (
                                <div key={idx} className="bracket-matchup">
                                    {renderTeamSlot(matchup.topSeed)}
                                    {renderTeamSlot(matchup.bottomSeed)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };
  return (
    <div className="AppGameList leaderboard">

      <div className="playoff-projection">


              <div className="share-card" ref={shareCardRef}>

                  
                  {!mSwitch && <div className="bracket-container">

                      {requiredPlayoffSpots === 32 && (
                          <div className="both-sides">
                              {renderBracket(rounds32a, 'bracket-8 bracket-16 bracket-32', true)}
                              {renderBracket(rounds32b, 'bracket-8 bracket-16 bracket-32 reversed', true, true)}
                          </div>
                      )}
                      {requiredPlayoffSpots === 24 && (
                          <div className="both-sides">
                              {renderBracket(rounds24a, '', false)}
                              {renderBracket(rounds24b, 'reversed', false, true)}
                          </div>
                      )}
                      {requiredPlayoffSpots === 16 && renderBracket(rounds16, 'bracket-8 bracket-16')}
                      {requiredPlayoffSpots === 8 && renderBracket(rounds8, 'bracket-8')}
                      {requiredPlayoffSpots === 4 && renderBracket(rounds4, 'bracket-8 bracket-4')}
                      {requiredPlayoffSpots === 12 && newRenderBracket(rounds_new, year)}

                  </div>}
        </div>
              {/*               <BubbleTeams teams={bubbleTeams} listOfTeams={listOfTeams} />

              {year === 2026 && (<div className={`slim-butt`} onClick={handleNativeShare}>
                  <Send size={18} strokeWidth={(2)} />&nbsp;{isSharing ? 'Generating...' : 'Share Projection'}
              </div>)}*/}
              <br />
              <br />
              <br />
              <br />

      </div>
    </div>
  );
};

export default PlayoffProjection;